(() => {
  if (customElements.get('nuora-review-feed')) return;

  // IP-2026-07-15-change: keep the compact Liquid/JS contract explicit and reject incompatible payloads safely.
  const REVIEW_DATA_VERSION = 1;

  class NuoraReviewFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // IP-2026-07-15-change: compact records and the inert card skeleton stay unparsed until another batch is requested.
      this.list = this.querySelector('[data-review-list]');
      this.deferredData = this.querySelector('[data-review-deferred]');
      this.cardTemplate = this.querySelector('[data-review-card-template]');
      this.deferredReviews = null;
      this.deferredParsed = false;
      this.defaultImageAlt = '';
      this.reviewTotals = this.parseReviewTotals();
      this.cards = Array.from(this.querySelectorAll('[data-review-card]'));
      this.groups = Array.from(this.querySelectorAll('[data-review-group]'));
      this.filters = Array.from(this.querySelectorAll('[data-review-filter]'));
      this.moreButton = this.querySelector('[data-review-more]');
      this.status = this.querySelector('[data-review-status]');
      this.initialCount = Number(this.dataset.initialCount || 6);
      this.batchSize = Number(this.dataset.batchSize || 6);
      this.initialFilter = this.dataset.initialFilter || '5';
      this.activeFilter = this.initialFilter;
      this.visibleLimit = this.initialCount;

      this.onClick = this.onClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      // IP-2026-07-15-change: Liquid supplies complete SSR in Theme Editor, preserving block.shopify_attributes and selection behavior.
      if (window.Shopify?.designMode) this.setAttribute('data-design-mode', '');

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.selectedGroup = null;
      this.editorState = null;
      this.initialized = false;
    }

    interpolate(template, values) {
      return Object.entries(values).reduce(
        (result, [key, value]) => result.split(`{${key}}`).join(String(value)),
        template || '',
      );
    }

    // IP-2026-07-15-change: totals remain available without parsing the deferred record payload during LCP.
    parseReviewTotals() {
      const values = String(this.dataset.reviewTotals || '')
        .split(',')
        .map((value) => Number(value));

      if (values.length !== 5 || values.some((value) => !Number.isFinite(value))) return null;

      return values.reduce((totals, value, index) => {
        totals[String(index + 1)] = value;
        return totals;
      }, {});
    }

    matchingCards(filter = this.activeFilter) {
      return this.cards.filter((card) => card.dataset.reviewStars === filter);
    }

    totalMatchingCards(filter = this.activeFilter) {
      const renderedTotal = this.matchingCards(filter).length;
      const configuredTotal = this.reviewTotals?.[filter];
      return Number.isFinite(configuredTotal) ? Math.max(configuredTotal, renderedTotal) : renderedTotal;
    }

    // IP-2026-07-15-change: parse and validate compact data once, but do not create any review DOM here.
    parseDeferredReviews() {
      if (this.deferredParsed) return this.deferredReviews || [];
      this.deferredParsed = true;

      if (!this.deferredData) {
        this.deferredReviews = [];
        return this.deferredReviews;
      }

      try {
        const payload = JSON.parse(this.deferredData.textContent || '{}');
        if (
          !payload
          || payload.v !== REVIEW_DATA_VERSION
          || !Array.isArray(payload.g)
        ) {
          throw new Error('Unsupported compact review payload.');
        }

        this.defaultImageAlt = typeof payload.a === 'string' ? payload.a : '';
        const records = [];

        payload.g.forEach((group) => {
          if (!Array.isArray(group) || group.length < 3 || !Array.isArray(group[2])) {
            throw new Error('Invalid compact review group.');
          }

          const groupKey = String(group[0] ?? '');
          const numericStars = Number(group[1]);
          if (!groupKey || !Number.isInteger(numericStars) || numericStars < 1 || numericStars > 5) {
            throw new Error('Invalid compact review group metadata.');
          }

          group[2].forEach((row) => {
            if (!Array.isArray(row) || row.length < 9 || typeof row[0] !== 'string' || !row[0]) {
              throw new Error('Invalid compact review record.');
            }

            records.push({
              groupKey,
              stars: String(numericStars),
              row,
              materialized: false,
            });
          });
        });

        this.deferredReviews = records;
        this.deferredData.remove();
        this.deferredData = null;
      } catch (error) {
        // IP-2026-07-15-change: fail closed to the six SSR cards instead of injecting partial or unsafe markup.
        console.error('Nuora reviews: compact review data could not be parsed.', error);
        this.deferredReviews = [];
        this.reviewTotals = null;
      }

      return this.deferredReviews;
    }

    // IP-2026-07-15-change: production-created groups mirror the original wrapper contract without editor-only attributes.
    ensureGroup(groupKey, stars) {
      let group = this.groups.find((candidate) => candidate.dataset.reviewGroupKey === groupKey);
      if (group) return group;
      if (!this.list) return null;

      group = document.createElement('div');
      group.className = 'nuora-reviews__group';
      group.dataset.reviewGroup = '';
      group.dataset.reviewGroupStars = stars;
      group.dataset.reviewGroupKey = groupKey;
      group.hidden = true;
      this.list.appendChild(group);
      this.groups.push(group);
      return group;
    }

    // IP-2026-07-15-change: clone one trusted skeleton and populate merchant content exclusively via textContent and attributes.
    createReviewCard(record) {
      const skeleton = this.cardTemplate?.content?.firstElementChild;
      if (!skeleton) return null;

      const card = skeleton.cloneNode(true);
      const row = record.row;
      const name = String(row[0] ?? '');
      const title = String(row[1] ?? '');
      const meta = String(row[2] ?? '');
      const body = String(row[3] ?? '');
      const imageSrc = String(row[4] ?? '');
      const imageSrcset = String(row[5] ?? '');
      const imageAlt = String(row[6] || this.defaultImageAlt || '');
      const imageWidth = Number(row[7]);
      const imageHeight = Number(row[8]);

      card.dataset.reviewStars = record.stars;
      card.hidden = true;

      const avatar = card.querySelector('[data-review-avatar]');
      const nameNode = card.querySelector('[data-review-name]');
      if (avatar) avatar.textContent = (Array.from(name.trim())[0] || '').toLocaleUpperCase();
      if (nameNode) nameNode.textContent = name;

      const setOptionalText = (selector, value) => {
        const node = card.querySelector(selector);
        if (!node) return;
        if (!value) {
          node.remove();
          return;
        }
        node.textContent = value;
      };

      setOptionalText('[data-review-title]', title);
      setOptionalText('[data-review-meta]', meta);
      setOptionalText('[data-review-body]', body);

      const rating = Number(record.stars);
      const ratingNode = card.querySelector('[data-review-rating]');
      if (ratingNode) {
        ratingNode.setAttribute(
          'aria-label',
          this.interpolate(this.dataset.reviewRatingTemplate, { rating }),
        );
        ratingNode.style.setProperty('--nuora-rating', String(rating));
        ratingNode.querySelectorAll('.nuora-stars__star').forEach((star, index) => {
          star.style.setProperty('--nuora-star-fill', index < rating ? '100%' : '0%');
        });
      }

      const image = card.querySelector('[data-review-photo]');
      if (!imageSrc) {
        image?.remove();
      } else if (image) {
        image.alt = imageAlt;
        if (Number.isFinite(imageWidth) && imageWidth > 0) image.width = Math.round(imageWidth);
        if (Number.isFinite(imageHeight) && imageHeight > 0) image.height = Math.round(imageHeight);
        if (imageSrcset) image.srcset = imageSrcset;
        image.src = imageSrc;
      }

      return card;
    }

    // IP-2026-07-15-change: materialize only the missing records for the requested visible limit (normally one batch of six).
    materializeReviews(filter, limit) {
      const needed = Math.max(0, limit - this.matchingCards(filter).length);
      if (needed === 0) return;

      const candidates = this.parseDeferredReviews()
        .filter((record) => !record.materialized && record.stars === filter)
        .slice(0, needed);

      candidates.forEach((record) => {
        const group = this.ensureGroup(record.groupKey, record.stars);
        const card = this.createReviewCard(record);
        if (!group || !card) return;

        group.appendChild(card);
        record.materialized = true;
        this.cards.push(card);
      });
    }

    setFilter(filter) {
      this.activeFilter = filter || this.initialFilter;
      this.visibleLimit = this.initialCount;
      this.selectedGroup = null;
      this.editorState = null;
      this.render();
    }

    onClick(event) {
      const filter = event.target.closest('[data-review-filter]');
      if (filter && this.contains(filter)) {
        this.setFilter(filter.dataset.reviewFilter);
        return;
      }

      if (!event.target.closest('[data-review-more]')) return;
      const total = this.totalMatchingCards();
      this.visibleLimit = Math.min(this.visibleLimit + this.batchSize, total);
      this.render();
    }

    onBlockSelect(event) {
      const group = event.target.closest('[data-review-group]');
      if (!group || !this.contains(group)) return;

      const card = event.target.closest('[data-review-card]') || group.querySelector('[data-review-card]');
      if (!card) return;

      if (!this.editorState) {
        this.editorState = {
          activeFilter: this.activeFilter,
          visibleLimit: this.visibleLimit,
        };
      }

      this.selectedGroup = group;
      this.activeFilter = group.dataset.reviewGroupStars || card.dataset.reviewStars || this.initialFilter;
      this.visibleLimit = this.matchingCards().length;
      this.render();

      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      card.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    onBlockDeselect(event) {
      const group = event.target.closest('[data-review-group]');
      if (!group || group !== this.selectedGroup) return;

      this.activeFilter = this.editorState?.activeFilter || this.initialFilter;
      this.visibleLimit = this.editorState?.visibleLimit || this.initialCount;
      this.selectedGroup = null;
      this.editorState = null;
      this.render();
    }

    render() {
      this.materializeReviews(
        this.activeFilter,
        Math.min(this.visibleLimit, this.totalMatchingCards()),
      );

      const total = this.totalMatchingCards();
      let shown = 0;

      this.cards.forEach((card) => {
        const matches = card.dataset.reviewStars === this.activeFilter;
        const visible = matches && shown < this.visibleLimit;
        card.hidden = !visible;
        if (visible) shown += 1;
      });

      if (window.Shopify?.designMode && this.selectedGroup) {
        this.selectedGroup.querySelectorAll('[data-review-card]').forEach((card) => {
          card.hidden = false;
        });
      }

      this.groups.forEach((group) => {
        const hasVisibleCard = Array.from(group.querySelectorAll('[data-review-card]'))
          .some((card) => !card.hidden);
        group.hidden = !hasVisibleCard;
      });

      this.filters.forEach((button) => {
        const selected = button.dataset.reviewFilter === this.activeFilter;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      if (this.status) {
        const activeButton = this.filters.find((button) => button.dataset.reviewFilter === this.activeFilter);
        this.status.textContent = this.interpolate(this.dataset.statusTemplate, {
          visible: shown,
          total,
          filter: activeButton?.dataset.reviewFilterLabel || '',
        });
      }

      if (!this.moreButton) return;
      const remaining = Math.max(0, total - shown);
      this.moreButton.hidden = remaining === 0;
      this.moreButton.setAttribute('aria-expanded', String(this.visibleLimit > this.initialCount));

      if (remaining > 0) {
        const count = Math.min(this.batchSize, remaining);
        this.moreButton.textContent = this.interpolate(this.dataset.moreTemplate, {
          count,
          reviews: count === 1 ? this.dataset.reviewSingular : this.dataset.reviewPlural,
        });
      }
    }
  }

  customElements.define('nuora-review-feed', NuoraReviewFeed);
})();
